import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { getISOWeek } from '../weekUtils';
import { StaffWeeklyReport, ensurePrintStyles } from './ReportsPanel';

function todayStr() { return new Date().toISOString().slice(0, 10); }
function currentYear() { return new Date().getFullYear(); }
function currentISOWeek() { return getISOWeek(todayStr()).week; }
function fmtDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}
function fmtCurrency(n) { return '$\u00a0' + Number(n || 0).toFixed(2); }
function fmtQty(n) {
  if (!n && n !== 0) return '1';
  if (n % 1 === 0) return String(n);
  return String(parseFloat(n.toFixed(2)));
}

// ── Mobile card layout ────────────────────────────────────────────────────────
function MobileJobsView({ emp, week }) {
  const isHourly = emp.is_hourly === true;
  // Use backend-tracked total_units (sum of scope × employee fraction per job),
  // not a sum of per-row units (which are always 1 now and would be inflated).
  const totalUnits = emp.total_units != null
    ? Math.round(emp.total_units * 100) / 100
    : Math.round(emp.jobs.reduce((s, r) => s + (parseFloat(r.units) || 1), 0) * 100) / 100;

  // Group jobs by date
  const byDate = {};
  emp.jobs.forEach(job => {
    const dk = job.date || '';
    if (!byDate[dk]) byDate[dk] = [];
    byDate[dk].push(job);
  });
  const dateKeys = Object.keys(byDate).sort((a, b) => a.localeCompare(b));

  const NAVY   = '#1e3a4a';
  const YELLOW = '#FFC000';
  const RED    = '#C00000';

  return (
    <div style={{ padding: '0 12px 80px' }}>
      {/* Employee header */}
      <div style={{ textAlign: 'center', padding: '20px 0 16px' }}>
        <div style={{ fontSize: 22, fontWeight: 900, color: NAVY, letterSpacing: 1 }}>
          {emp.employee_name.toUpperCase()}
        </div>
        <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4, letterSpacing: 2 }}>
          STAFF WEEKLY · WEEK {week}
        </div>
      </div>

      {/* Job cards grouped by date */}
      {dateKeys.map(dk => {
        // Daily totals — pending jobs excluded to match the weekly grand total.
        const approvedForDay = byDate[dk].filter(j => j.status !== 'pending');
        const dayUnits  = approvedForDay.reduce((s, j) => s + (parseFloat(j.units) || 1), 0);
        const dayPayout = approvedForDay.reduce((s, j) => s + parseFloat(j.payout || 0), 0);
        const dayHasPending = byDate[dk].some(j => j.status === 'pending');

        return (
        <div key={dk} style={{ marginBottom: 16 }}>
          {/* Date header */}
          <div style={{
            background: NAVY, color: '#fff', padding: '6px 14px',
            borderRadius: '8px 8px 0 0', fontSize: 12, fontWeight: 700, letterSpacing: 0.5,
          }}>
            {fmtDate(dk)}
          </div>

          {byDate[dk].map((job, i) => {
            const isPending = job.status === 'pending';
            return (
              <div key={i} style={{
                background: isPending ? '#fffbeb' : '#fff',
                borderLeft: `3px solid ${isPending ? '#f59e0b' : '#4A7A91'}`,
                borderBottom: '1px solid #f0f0f0', padding: '10px 14px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  {/* Left: stock + model + service */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#1f2937' }}>
                      {job.service || job.job_description}
                    </div>
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                      {[job.stock_number, job.model].filter(Boolean).join(' · ')}
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px',
                        background: '#f0f4f8', borderRadius: 10, fontSize: 10,
                        color: '#4A7A91', fontWeight: 600,
                      }}>
                        {job.department}
                      </span>
                      {isPending && (
                        <span style={{
                          display: 'inline-block', padding: '2px 8px',
                          background: '#fef3c7', border: '1px solid #fcd34d',
                          borderRadius: 10, fontSize: 10, color: '#92400e', fontWeight: 700,
                        }}>
                          ⏳ Aguardando Aprovação
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Right: QTY + payout (payout hidden for hourly or if pending) */}
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>QTY</div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#1f2937' }}>
                      {fmtQty(parseFloat(job.units) || 1)}
                    </div>
                    {!isHourly && (
                      <div style={{ fontSize: 13, fontWeight: 700, color: isPending ? '#d97706' : '#16a34a', marginTop: 2 }}>
                        {isPending ? 'Pendente' : fmtCurrency(job.payout)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Daily subtotal — pending jobs NOT included so it matches the weekly total */}
          <div style={{
            background: '#f1f5f9',
            borderLeft: `3px solid ${NAVY}`,
            borderTop: '1px solid #e5e7eb',
            padding: '8px 14px',
            borderRadius: '0 0 8px 8px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.8, color: '#64748b' }}>
                TOTAL DO DIA
              </span>
              {dayHasPending && (
                <span style={{ fontSize: 9, color: '#d97706' }}>
                  (pendentes não contados)
                </span>
              )}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: '#6b7280' }}>
                QTY <strong style={{ color: '#1f2937' }}>{fmtQty(dayUnits)}</strong>
              </div>
              {!isHourly && (
                <div style={{ fontSize: 15, fontWeight: 800, color: '#16a34a', marginTop: 2 }}>
                  {fmtCurrency(dayPayout)}
                </div>
              )}
            </div>
          </div>
        </div>
        );
      })}

      {/* Summary footer */}
      <div style={{ marginTop: 8, borderRadius: 10, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        {/* Subtotal */}
        <div style={{ background: '#f2f2f2', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 600, fontSize: 12, color: '#555' }}>
            {isHourly
              ? `${emp.days_worked} ${emp.days_worked === 1 ? 'day' : 'days'} × ${fmtCurrency(emp.daily_wage)}`
              : `Total QTY: ${fmtQty(totalUnits)}`
            }
          </span>
          {!isHourly && (
            <span style={{ fontWeight: 700, fontSize: 13, color: '#374151' }}>
              {fmtCurrency(emp.total_payout)}
            </span>
          )}
        </div>
        {/* Grand total */}
        <div style={{
          background: YELLOW, padding: '14px 16px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{ fontWeight: 900, fontSize: 13, color: RED }}>TOTAL PAYOUT</span>
          <span style={{ fontWeight: 900, fontSize: 20, color: RED }}>
            {fmtCurrency(isHourly ? emp.weekly_pay : emp.total_payout)}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────
export default function MyJobsPanel({ user }) {
  // Default to the CURRENT week so freshly submitted jobs appear immediately
  const defaultWeek = currentISOWeek();
  const defaultYear = currentYear();

  const [selectedWeek, setSelectedWeek] = useState(defaultWeek);
  const [selectedYear, setSelectedYear] = useState(defaultYear);
  const [staffData,    setStaffData]    = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);
  const [isMobile,     setIsMobile]     = useState(window.innerWidth <= 768);

  useEffect(() => {
    ensurePrintStyles();
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function doFetch() {
      setLoading(true); setError(null);
      try {
        const data = await api.getStaffWeekly(selectedWeek, selectedYear);
        if (!cancelled) {
          const myEntry = (data.staff || []).filter(
            e => e.employee_id === user.id || e.employee_name?.toLowerCase() === user.name?.toLowerCase()
          );
          setStaffData({ ...data, staff: myEntry });
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    doFetch();
    return () => { cancelled = true; };
  }, [selectedWeek, selectedYear, user.id, user.name]);

  const weekOptions = [];
  for (let w = 1; w <= 52; w++) weekOptions.push(w);

  const selStyle = {
    padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db',
    fontSize: 14, background: '#fff', color: '#1f2937', fontWeight: 600,
  };

  return (
    <div style={{ paddingBottom: 80 }}>
      {/* Week / Year selector */}
      <div style={{
        display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
        padding: '12px 16px', background: '#f8f9fb',
        borderBottom: '1px solid #e5e7eb',
      }}>
        <label style={{ fontWeight: 600, fontSize: 13, color: '#374151' }}>Week:</label>
        <select value={selectedWeek} onChange={e => setSelectedWeek(parseInt(e.target.value))} style={selStyle}>
          {weekOptions.map(w => <option key={w} value={w}>Week {w}</option>)}
        </select>
        <label style={{ fontWeight: 600, fontSize: 13, color: '#374151' }}>Year:</label>
        <select value={selectedYear} onChange={e => setSelectedYear(parseInt(e.target.value))} style={selStyle}>
          {[currentYear() - 1, currentYear(), currentYear() + 1].map(y =>
            <option key={y} value={y}>{y}</option>
          )}
        </select>
      </div>

      {loading && <p style={{ textAlign: 'center', color: '#999', padding: 40 }}>Loading...</p>}
      {error   && <p style={{ textAlign: 'center', color: '#dc2626', padding: 40 }}>Error: {error}</p>}

      {!loading && !error && staffData && (
        staffData.staff.length === 0
          ? <p style={{ textAlign: 'center', color: '#9ca3af', padding: 40, fontSize: 14 }}>
              No jobs found for Week {selectedWeek}, {selectedYear}.
            </p>
          : <>
              {/* Pending notice banner */}
              {staffData.staff.some(e => e.jobs.some(j => j.status === 'pending')) && (
                <div style={{
                  margin: '12px 16px 0', padding: '10px 16px',
                  background: '#fef3c7', border: '1px solid #fcd34d',
                  borderRadius: 8, fontSize: 13, color: '#92400e',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <span style={{ fontSize: 18 }}>⏳</span>
                  <span>Alguns jobs estão <strong>aguardando aprovação</strong> do admin e não contam no total ainda.</span>
                </div>
              )}
              {isMobile
                ? <MobileJobsView emp={staffData.staff[0]} week={selectedWeek} />
                : <StaffWeeklyReport data={staffData} week={selectedWeek} year={selectedYear} isAdmin={false} showDailyTotals={true} />
              }
            </>
      )}
    </div>
  );
}
